// fronted/src/pages/WalletRisk.js
import React, { useState, useEffect } from "react";
import axios from "../utils/axiosConfig"; // 新增 CSRF 設定
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom"; // 新增 useLocation
import API_URL from "../config";
import { FaMagnifyingGlass } from "react-icons/fa6"; // Font Awesome 6 (FA6)
import { toast } from "react-toastify";

function WalletRisk() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation(); // 取得從首頁傳來的 state

  // 使用 state 儲存錢包地址、鏈代號與其他查詢資料
  const [walletAddress, setWalletAddress] = useState("");
  const [chainId, setChainId] = useState(1); // 預設使用 Ethereum (1)
  const [riskData, setRiskData] = useState(null);
  const [sanctionData, setSanctionData] = useState(null);
  const [comment, setComment] = useState(""); // 新增備註欄位
  const [label, setLabel] = useState("scam"); // 預設標籤
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userId, setUserId] = useState(null);
  const [complianceRiskData, setComplianceRiskData] = useState(null);

  // 定義各鏈的地址規則，用於自動判斷
  const chainAddressRules = {
    // 非 EVM 鏈（獨特格式）
    '-2': {
      regex: /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
      name: 'TRON',
      priority: 1 // 最高優先級，因為格式獨特
    },
    '-1': {
      regex: /^(1[1-9A-HJ-NP-Za-km-z]{25,34}|3[1-9A-HJ-NP-Za-km-z]{25,34}|bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39})$/,
      name: 'Bitcoin',
      priority: 2,
      exclude: /^T[1-9A-HJ-NP-Za-km-z]{33}$/ // 排除 TRON 地址
    },
    '-3': {
      regex: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
      name: 'Solana',
      priority: 3,
      exclude: /^(T[1-9A-HJ-NP-Za-km-z]{33}|bc1[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{39})$/ // 排除 TRON 和 Bitcoin Bech32
    },
    // EVM 兼容鏈（統一格式）
    'evm': {
      regex: /^0x[a-fA-F0-9]{40}$/,
      name: 'EVM Compatible',
      priority: 4,
      chains: [
        { chainId: '1', name: 'Ethereum' },
        { chainId: '10', name: 'Optimism' },
        { chainId: '25', name: 'Cronos' },
        { chainId: '56', name: 'BSC' },
        { chainId: '100', name: 'Gnosis' },
        { chainId: '137', name: 'Polygon' },
        { chainId: '169', name: 'Manta Pacific' },
        { chainId: '199', name: 'Bittorrent' },
        { chainId: '250', name: 'Fantom' },
        { chainId: '288', name: 'Boba' },
        { chainId: '324', name: 'zkSync Era' },
        { chainId: '1024', name: 'CLV' },
        { chainId: '1101', name: 'Polygon zkEVM' },
        { chainId: '1111', name: 'Wemix' },
        { chainId: '1284', name: 'Moonbeam' },
        { chainId: '1285', name: 'Moonriver' },
        { chainId: '5000', name: 'Mantle' },
        { chainId: '8453', name: 'Base' },
        { chainId: '42161', name: 'Arbitrum' },
        { chainId: '42220', name: 'Celo' },
        { chainId: '43114', name: 'Avalanche' },
        { chainId: '59144', name: 'Linea' },
        { chainId: '81457', name: 'Blast' },
        { chainId: '1313161554', name: 'Aurora' }
      ]
    }
  };

  // 自動判斷鏈的函數
  const detectChain = (address) => {
    if (!address) return 1; // 如果地址為空，預設為 Ethereum
    for (const [id, rule] of Object.entries(chainAddressRules)) {
      const matches = rule.regex.test(address);
      const excluded = rule.exclude ? rule.exclude.test(address) : false;
      if (matches && !excluded) {
        if (id === 'evm') {
          // 如果是 EVM 地址，返回第一個鏈（Ethereum）作為預設
          return parseInt(rule.chains[0].chainId); // 預設返回 Ethereum (1)
        }
        return parseInt(id);
      }
    }
    return 1; // 如果無法匹配，預設為 Ethereum
  };

  // 檢查使用者認證狀態
  useEffect(() => {
    async function checkAuth() {
      try {
        const response = await axios.get(`${API_URL}/api/current_user`, { withCredentials: true });
        if (response.data) {
          setIsAuthenticated(true);
          setUserId(response.data._id);
        } else {
          setIsAuthenticated(false);
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    }
    checkAuth();
  }, []);

  // 當 walletAddress 變化時，自動判斷鏈
  useEffect(() => {
    const detectedChainId = detectChain(walletAddress);
    setChainId(detectedChainId);
  }, [walletAddress]);

  // 將查詢邏輯抽離成獨立函式，方便表單提交與自動查詢共用
  const queryWalletRisk = async (address, chain) => {
    if (!address) return; // 若 address 為空則不查詢
    console.log("Querying wallet risk for:", address, chain);
    // 初始化臨時變數
    let riskDataTemp = null;
    let sanctionDataTemp = null;
    let complianceRiskDataTemp = null;

    try {
      // 查詢風險標籤資料
      try {
        const riskResponse = await axios.post(
          `${API_URL}/api/predict-risk`,
          { walletAddress: address, chain_id: chain },
          { withCredentials: true }
        );
        riskDataTemp = riskResponse.data;
      } catch (error) {
        console.error("Error fetching risk labels:", error);
        toast.error("風險標籤查詢失敗或用量達上限。");
      }

      // 查詢制裁訊息
      try {
        const sanctionResponse = await axios.get(`${API_URL}/api/check-sanction/${address}`);
        sanctionDataTemp = sanctionResponse.data;
      } catch (error) {
        console.error("Error fetching sanction data:", error);
        toast.error("制裁查詢失敗或用量達上限。");
      }

      // 查詢合規風險分數
      try {
        const complianceResponse = await axios.post(
          `${API_URL}/api/compliance-risk`,
          { walletAddress: address, chain_id: chain },
          { withCredentials: true }
        );
        complianceRiskDataTemp = complianceResponse.data;
      } catch (error) {
        console.error("Error fetching compliance risk score:", error);
        toast.error("合規風險分數查詢失敗或用量達上限。");
      }

      // 更新 state
      setRiskData(riskDataTemp);
      setSanctionData(sanctionDataTemp);
      setComplianceRiskData(complianceRiskDataTemp);

      // 儲存查詢紀錄（若有資料則儲存）
      let saveData = {
        walletAddress: address,
        chainId: chain,
        updatedAt: new Date()
      };
      if (riskDataTemp) {
        saveData.riskLabels = riskDataTemp;
      }
      if (sanctionDataTemp) {
        saveData.sanctionStatus = sanctionDataTemp;
      }
      if (complianceRiskDataTemp) {
        saveData.complianceScore = complianceRiskDataTemp;
      }
      if (Object.keys(saveData).length > 2) {
        await axios.post(`${API_URL}/api/save-query-history`, saveData);
      } else {
        console.log("無需儲存，沒有可儲存的有效查詢結果");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      toast.error("無法查詢該地址的資訊。");
    }
  };

  // 表單提交時呼叫查詢函式
  const handleSubmit = async (e) => {
    e.preventDefault();
    await queryWalletRisk(walletAddress, chainId); // 傳入地址與鏈代號
  };

  // 若從首頁傳入 state，則自動填入地址與鏈代號，並執行查詢
  useEffect(() => {
    if (location.state && location.state.walletAddress) {
      setWalletAddress(location.state.walletAddress);
      const detectedChainId = detectChain(location.state.walletAddress);
      setChainId(detectedChainId); // 根據地址自動判斷鏈
      queryWalletRisk(location.state.walletAddress, detectedChainId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // 新增至白名單功能
  const handleAddToWhitelist = async () => {
    if (!walletAddress) {
      toast.error(t("enterValidWalletAddress"));
      return;
    }
    if (!isAuthenticated) {
      window.location.href = `${API_URL}/api/auth/google`;
      return;
    }
    try {
      await axios.post(`${API_URL}/api/whitelist`, { walletAddress, userId, comment });
      toast.success(t("walletAddedToWhitelist"));
    } catch (error) {
      toast.error(t("errorAddingWalletToWhitelist"));
    }
  };

  // 新增至黑名單功能
  const handleAddToBlacklist = async () => {
    if (!walletAddress) {
      toast.error(t("enterValidWalletAddress"));
      return;
    }
    if (!isAuthenticated) {
      window.location.href = `${API_URL}/api/auth/google`;
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/api/blacklist`, {
        walletAddress,
        riskLevel: "low", // 預設風險等級
        blackScore: 1,    // 初始分數
        labels: [label],  // 選定標籤
        comment,          // 備註
        userId,
      });

      // 更新用戶資訊中的「舉報的可疑錢包」
      const userResponse = await axios.post(`${API_URL}/api/add_suspicious_wallet_to_user`, {
        userId,
        walletAddress,
        label,
        comment
      });

      if (userResponse.status === 200) {
        toast.success(t("walletAddedToBlacklist"));
      }
    } catch (error) {
      if (error.response && error.response.status === 400 && error.response.data.error === "已經回報過該地址") {
        toast.error(error.response.data.error);
      } else {
        toast.error(t("errorAddingWalletToBlacklist"));
      }
    }
  };

  // 取得 riskData 中各欄位的資料，若無則預設為 t("unknown")
  const mainEntity =
    riskData && riskData.data && riskData.data.main_entity
      ? riskData.data.main_entity
      : t("unknown");
  const nameTag =
    riskData && riskData.data && riskData.data.name_tag
      ? riskData.data.name_tag
      : t("unknown");
  const attributes =
    riskData && riskData.data && riskData.data.attributes
      ? riskData.data.attributes
      : [];
  const riskScore =
    complianceRiskData && complianceRiskData.risk_score
      ? complianceRiskData.risk_score
      : t("unknown");
  const riskIndicators =
    complianceRiskData && complianceRiskData.risk_indicators
      ? complianceRiskData.risk_indicators
      : t("unknown");

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 min-h-screen">
      <div
        className="w-full h-64 bg-cover bg-center flex flex-col items-center justify-center text-white text-3xl font-bold shadow-lg"
        style={{ backgroundImage: "url('/images/myReportsBanner.png')" }}
      >
        <h1 className="text-white uppercase tracking-wide">
          {t("walletRiskQuery")}
        </h1>
        <p className="text-lg font-medium mt-2 text-gray-200">
          {t("walletRiskAssessment")}
        </p>
      </div>

      <div className="flex justify-center items-center py-12">
        <form onSubmit={handleSubmit} className="w-full max-w-2xl">
          <div className="flex h-12 rounded-xl shadow-md border border-purple-200/50 dark:border-purple-700/30 overflow-hidden">
            {/* 輸入框 */}
            <div className="relative flex-1">
              <input
                type="text"
                placeholder={t("inputWalletAddress")}
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                className="w-full px-4 py-2 pl-10 bg-white dark:bg-gray-800 text-black dark:text-white border-none focus:outline-none focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 placeholder-gray-400 dark:placeholder-gray-500 h-full box-border leading-[2rem] transition-all"
              />
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaMagnifyingGlass className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </span>
            </div>
            {/* 鏈選擇下拉選單 */}
            <select
              value={chainId}
              onChange={(e) => setChainId(parseInt(e.target.value))}
              className="px-3 py-2 bg-white dark:bg-gray-800 text-black dark:text-white border-l border-purple-200/50 dark:border-purple-700/30 focus:outline-none focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 h-full box-border leading-[2rem] transition-all"
            >
              <option value="-3">Solana</option>
              <option value="-2">TRON</option>
              <option value="-1">Bitcoin</option>
              <option value="1">Ethereum</option>
              <option value="10">Optimism</option>
              <option value="25">Cronos</option>
              <option value="56">BSC</option>
              <option value="100">Gnosis</option>
              <option value="137">Polygon</option>
              <option value="169">Manta Pacific</option>
              <option value="199">Bittorrent</option>
              <option value="250">Fantom</option>
              <option value="288">Boba</option>
              <option value="324">zkSync Era</option>
              <option value="1024">CLV</option>
              <option value="1101">Polygon zkEVM</option>
              <option value="1111">Wemix</option>
              <option value="1284">Moonbeam</option>
              <option value="1285">Moonriver</option>
              <option value="5000">Mantle</option>
              <option value="8453">Base</option>
              <option value="42161">Arbitrum</option>
              <option value="42220">Celo</option>
              <option value="43114">Avalanche</option>
              <option value="59144">Linea</option>
              <option value="81457">Blast</option>
              <option value="1313161554">Aurora</option>

            </select>
            {/* 搜尋按鈕 */}
            <button
              type="submit"
              className="px-6 bg-gradient-to-r from-purple-400 to-indigo-500 dark:from-purple-500 dark:to-violet-600 text-white font-bold rounded-r-xl hover:from-purple-500 hover:to-indigo-600 dark:hover:from-purple-600 dark:hover:to-violet-700 focus:outline-none focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 h-full box-border leading-[2rem] flex items-center justify-center transition-all whitespace-nowrap"
            >
              {t("search")}
            </button>
          </div>
        </form>
      </div>

      {/* 查詢結果展示區塊 */}
      <div className="container mx-auto py-12">
        <div className="space-y-8">
          {/* 錢包風險詳情 */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-purple-200/50 dark:border-purple-700/30">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-indigo-500 bg-clip-text text-transparent dark:from-purple-300 dark:to-violet-400">
              {t("walletRiskDetails")}
            </h2>
            {riskData ? (
              <>
                <p>
                  <strong className="text-gray-700 dark:text-gray-300">{t("mainEntity")}:</strong>{" "}
                  {mainEntity || "N/A"}
                </p>
                <p>
                  <strong className="text-gray-700 dark:text-gray-300">{t("nameTag")}:</strong>{" "}
                  {nameTag || "N/A"}
                </p>
                {riskData.data && riskData.data.attributes && riskData.data.attributes.length > 0 ? (
                  <ul className="list-disc pl-5 mt-2">
                    {riskData.data.attributes.map((attr, index) => (
                      <li key={index} className="text-gray-700 dark:text-gray-300">
                        {attr.name.includes("BLOCKED") ? (
                          <span className="text-red-500 font-bold">{t("blocked")}</span>
                        ) : (
                          attr.name
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 mt-2">{t("noWalletTagsAvailable")}</p>
                )}
              </>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">{t("noWalletDataYet")}</p>
            )}
          </div>

          {/* 制裁檢查結果 */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-purple-200/50 dark:border-purple-700/30">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-indigo-500 bg-clip-text text-transparent dark:from-purple-300 dark:to-violet-400">
              {t("sanctionCheckResult")}
            </h2>
            {sanctionData ? (
              sanctionData.is_sanctioned === true ? (
                <div>
                  <p className="text-red-500 font-bold">{t("walletSanctioned")}</p>
                  {sanctionData.identifications && sanctionData.identifications.length > 0 && (
                    <p className="text-gray-700 dark:text-gray-300">
                      <strong>{t("sanctionSource")}:</strong> {sanctionData.identifications[0].name}
                    </p>
                  )}
                </div>
              ) : sanctionData.is_sanctioned === false ? (
                <p className="text-gray-700 font-bold">{t("walletNotSanctioned")}</p>
              ) : (
                <p className="text-gray-700 font-bold">{t("unknown")}</p>
              )
            ) : (
              <p className="text-gray-500 dark:text-gray-400">{t("noSanctionDataYet")}</p>
            )}
          </div>

          {/* 合規風險評分 */}
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-purple-200/50 dark:border-purple-700/30">
            <h2 className="text-2xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-indigo-500 bg-clip-text text-transparent dark:from-purple-300 dark:to-violet-400">
              {t("complianceRiskScore")}
            </h2>
            {complianceRiskData ? (
              <>
                <p className="text-gray-700 dark:text-gray-300">
                  <strong>{t("riskScore")}:</strong> {riskScore || "N/A"}
                </p>
                <div className="mt-2">
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>{t("riskIndicators")}:</strong>
                  </p>
                  {Array.isArray(riskIndicators) && riskIndicators.length > 0 ? (
                    <ul className="list-disc pl-5">
                      {riskIndicators.map((indicatorObj, index) => (
                        <div key={index} className="mb-2 text-gray-700 dark:text-gray-300">
                          {typeof indicatorObj.indicator === "object" ? (
                            <div className="ml-5">
                              <li>
                                <strong>{t("indicatorName")}:</strong> {indicatorObj.indicator.name}
                              </li>
                              <li>
                                <strong>{t("indicatorCode")}:</strong> {indicatorObj.indicator.code}
                              </li>
                            </div>
                          ) : (
                            <p>{indicatorObj.indicator}</p>
                          )}
                        </div>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">{t("noRiskIndicators")}</p>
                  )}
                </div>
              </>
            ) : (
              <p className="text-gray-500 dark:text-gray-400">{t("noComplianceDataYet")}</p>
            )}
          </div>

          {/* 備註與按鈕 */}
          <div className="mt-6 flex flex-col space-y-4 md:space-y-0 md:flex-row md:space-x-4">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={t("enterComment")}
              className="w-full md:w-auto p-3 border rounded-xl dark:bg-gray-800 dark:text-white dark:border-gray-700 resize-none border-purple-200/50 dark:border-purple-700/30 focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 transition-all"
            />
            <button
              onClick={handleAddToWhitelist}
              className="bg-gradient-to-r from-green-400 to-green-600 dark:from-green-500 dark:to-green-700 text-white px-4 py-2 rounded-xl shadow-md hover:from-green-500 hover:to-green-700 dark:hover:from-green-600 dark:hover:to-green-800 focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 transition-all"
            >
              {t("thisIsMyWallet")}
            </button>
            <button
              onClick={handleAddToBlacklist}
              className="bg-gradient-to-r from-red-400 to-red-600 dark:from-red-500 dark:to-red-700 text-white px-4 py-2 rounded-xl shadow-md hover:from-red-500 hover:to-red-700 dark:hover:from-red-600 dark:hover:to-red-800 focus:ring-2 focus:ring-purple-300 dark:focus:ring-violet-500 transition-all"
            >
              {t("thisIsSuspiciousWallet")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WalletRisk;